'use client';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { IconChevronDown } from '../../../../design-system/components/icons/ChevronDown';
import { EvaneosPrismicRichText } from '../../../../domains/Prismic/EvaneosPrismicRichText';
import { HomePageDocument } from '../../../../resources/homePageDocument/domain/types/homePageDocument';
import { useTrackBlockViewed } from '../../../../shared/hooks/useTrackBlockViewed';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';
import { queryClient } from '../../providers/reactQuery';
import { WhereToGoButton } from '../WhereToGoButton/WhereToGoButton';

import style from './CoverSection.module.css';
import ReassuranceBanner from './ReassuranceBanner/ReassuranceBanner';
import { SearchBar } from './SearchBar/SearchBar';

const IMAGE_DISPLAY_DURATION_MS = 5000;

type CoverSectionProps = PickDomain<HomePageDocument, 'cover'>;

export function CoverSection({ cover }: CoverSectionProps) {
    const { formatMessage } = useIntl();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const sectionRef = useRef<HTMLDivElement>(null);

    const { backgroundImages, title } = cover;

    useTrackBlockViewed(sectionRef, 'cover');

    useEffect(() => {
        if (backgroundImages.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        }, IMAGE_DISPLAY_DURATION_MS);

        return () => clearInterval(interval);
    }, [backgroundImages.length]);

    const handleScrollToReasons = () => {
        window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth',
        });
    };

    return (
        <section ref={sectionRef} className="layout-with-centered-content">
            <div className="main-container-cover">
                <div className={style.container}>
                    <div className={style.backgroundImage}>
                        {backgroundImages.map((image, index) => {
                            const srcSet = `${image['375x211'].url} 375w, ${image['384x216'].url} 384w, ${image['480x270'].url} 480w, ${image['576x324'].url} 576w, ${image['768x432'].url} 768w, ${image['992x558'].url} 992w, ${image['1200x675'].url} 1200w, ${image['1400x788'].url} 1400w, ${image['1920x1080'].url} 1920w, ${image['2048x1152'].url} 2048w, ${image['3840x2160'].url} 3840w`;

                            return (
                                <motion.div
                                    key={image.url}
                                    initial={false}
                                    animate={{ opacity: index === currentImageIndex ? 1 : 0 }}
                                    transition={{
                                        duration: 1.5,
                                        ease: 'easeInOut',
                                    }}
                                >
                                    <img
                                        className={style.backgroundImageImg}
                                        src={image['375x211'].url}
                                        alt={image.alt}
                                        sizes="100vw"
                                        srcSet={srcSet}
                                        loading="eager"
                                    />
                                </motion.div>
                            );
                        })}
                        <div className={style.overlay} />
                    </div>

                    <div className={style.heroSection}>
                        <div className={style.titleWrapper}>
                            <div className={style.title}>
                                <EvaneosPrismicRichText field={title} components={{}} />
                            </div>
                            <h1 className={style.subtitle}>
                                {formatMessage({ id: 'page.home.coverSubtitle' })}
                            </h1>
                        </div>

                        <div className={style.searchWrapper}>
                            <QueryClientProvider client={queryClient}>
                                <SearchBar />
                                <ReactQueryDevtools initialIsOpen={false} />
                            </QueryClientProvider>

                            <WhereToGoButton />
                        </div>

                        <button
                            type="button"
                            onClick={handleScrollToReasons}
                            className={style.scrollDownButton}
                        >
                            <IconChevronDown className={style.iconScrollDown} />
                        </button>
                    </div>

                    <ReassuranceBanner className={style.reassuranceBanner} />
                </div>
            </div>
        </section>
    );
}
